import {
  ClockClockwiseIcon,
  IdentificationBadgeIcon,
  IdentificationCardIcon,
  Icon as PhosphorIcon,
  UsersIcon,
} from '@phosphor-icons/react'
import { TFunction } from 'i18next'

import { generateTimeOptions } from '@/utils'

export const MIN_VISIT_DURATION = 15

import {
  AccessType,
  AccessTypeEnum,
  PermissionType,
  PermissionTypeEnum,
  VisitorType,
  VisitorTypeEnum,
} from './add-visitor-modal.types'

export const getVisitorTypeOptions = (
  t: TFunction<['visitors']>,
): Array<{
  value: VisitorType
  label: string
  icon: PhosphorIcon
}> => [
  { icon: UsersIcon, label: t('visitors:modalTypes.guest'), value: VisitorTypeEnum.enum.guest },
  {
    icon: IdentificationBadgeIcon,
    label: t('visitors:modalTypes.vendor'),
    value: VisitorTypeEnum.enum.vendor,
  },
]

export const getAccessTypeOptions = (
  t: TFunction<['visitors']>,
): Array<{ value: AccessType; label: string; icon: PhosphorIcon }> => [
  {
    icon: ClockClockwiseIcon,
    label: t('visitors:dashboard.accessTypeOneTime'),
    value: AccessTypeEnum.enum['one-time'],
  },
  {
    icon: IdentificationCardIcon,
    label: t('visitors:dashboard.accessTypeRecurring'),
    value: AccessTypeEnum.enum.recurring,
  },
]

// `value` is persisted verbatim as the guest's `type` (see formatValues in
// add-visitor-modal.utils.ts, which reads `.label` off this exact array into
// the API payload's `text` field), not just display copy — stays untranslated.
// Use `getGuestCategoryOptions` for translated labels shown in the select.
export const GUEST_CATEGORY_OPTIONS = [
  { label: 'Friend', value: 'Friend' },
  { label: 'Relative', value: 'relative' },
  { label: 'Personal Trainer', value: 'personalTrainer' },
  { label: 'Housekeeper', value: 'housekeeper' },
  { label: 'Handyman', value: 'handyman' },
  { label: 'Dog Walker', value: 'dogWalker' },
  { label: 'Other', value: 'other' },
]

export const getGuestCategoryOptions = (t: TFunction<['visitors']>) => [
  { label: t('visitors:recipientTypes.friend'), value: 'Friend' },
  { label: t('visitors:recipientTypes.relative'), value: 'relative' },
  { label: t('visitors:recipientTypes.personalTrainer'), value: 'personalTrainer' },
  { label: t('visitors:recipientTypes.housekeeper'), value: 'housekeeper' },
  { label: t('visitors:recipientTypes.handyman'), value: 'handyman' },
  { label: t('visitors:recipientTypes.dogWalker'), value: 'dogWalker' },
  { label: t('visitors:recipientTypes.other'), value: 'other' },
]

export const getPermissionTypeOptions = (
  t: TFunction<['visitors']>,
): Array<{ value: PermissionType; label: string }> => [
  { label: t('visitors:dashboard.permissionTypeOneTime'), value: PermissionTypeEnum.enum.custom },
  { label: t('visitors:permissions.daily'), value: PermissionTypeEnum.enum.daily },
  { label: t('visitors:permissions.weekly'), value: PermissionTypeEnum.enum.weekly },
  { label: t('visitors:permissions.monthly'), value: PermissionTypeEnum.enum.monthly },
  { label: t('visitors:dashboard.permissionTypeAlways'), value: PermissionTypeEnum.enum.always },
]

export const getWeekdays = (t: TFunction<['visitors']>) => [
  { index: 0, label: t('visitors:dayAbbreviations.su') },
  { index: 1, label: t('visitors:dayAbbreviations.mo') },
  { index: 2, label: t('visitors:dayAbbreviations.tu') },
  { index: 3, label: t('visitors:dayAbbreviations.we') },
  { index: 4, label: t('visitors:dayAbbreviations.th') },
  { index: 5, label: t('visitors:dayAbbreviations.fr') },
  { index: 6, label: t('visitors:dayAbbreviations.sa') },
]

export const MAX_COI_SIZE = 10 * 1_000_000 // 10MB
export const COI_ACCEPTED_TYPES = ['pdf']

export const TIME_OPTIONS = generateTimeOptions(MIN_VISIT_DURATION)
